/**
 * "Költség rögzítése" — the driver logs something they paid out of pocket:
 * a parking ticket at the airport, or anything else ("Egyéb").
 *
 * Opened from the day screen (free-standing) or from a trip (then `bookingId`
 * links it to that booking — airport parking belongs to that trip).
 *
 * The amount here is the one piece of money the portal ever shows: it is the
 * driver's own claim, typed by them, never a booking price.
 *
 * The receipt photo is taken with the camera directly (`capture="environment"`)
 * and downscaled in the browser before upload — see imageDownscale.js for why.
 *
 * THE PHOTO IS READ BEFORE THE DRIVER SUBMITS
 * -------------------------------------------
 * As soon as a photo has been downscaled it goes to `/portal/expenses/scan`,
 * which reads the total, the date and the merchant off it and says whether
 * this photo has been filed before. Three rules hold that feature together,
 * and none of them is an accident:
 *
 *  1. THE READING FILLS THE AMOUNT, IT NEVER LOCKS IT. What the driver typed
 *     is what gets submitted, every time. The prefill is a convenience and
 *     the hint under the box says so out loud. A locked field would make the
 *     model the author of a money claim the driver signs — and the model is
 *     wrong often enough that the dispatcher has a whole badge for it.
 *  2. ONLY AN UNREADABLE PHOTO INTERRUPTS, AND EVEN THEN IT IS A SPEED BUMP.
 *     "Új fotó készítése" is the loud action; "Beküldöm így is" is always
 *     there underneath it. Trapping a driver whose receipt is genuinely
 *     crumpled loses the receipt entirely, which is far worse than a typo.
 *  3. A SCAN THAT FAILED AND A PHOTO THAT CANNOT BE READ ARE THE SAME SCREEN.
 *     A 500 or a dropped connection is mapped to `scan_unavailable` right
 *     here, and that is the ONE place that mapping lives. The driver has no
 *     use for the difference; the dispatcher is told which it was by the
 *     row's own badge (Utils/tripClaims's receiptScanBadge).
 *
 * The submit body is UNCHANGED by all of this: no hash, no reading. The
 * server recomputes the hash from the bytes it receives and looks the reading
 * up itself, because a client-supplied total would then be vouched for by a
 * badge the dispatcher trusts.
 */
import React, { useCallback, useRef, useState } from 'react';
import { createPortalExpense, scanPortalExpensePhoto } from '../../../Services/PortalApi';
import { downscaleImage } from '../imageDownscale';
import { EXPENSE_COPY, EXPENSE_CURRENCIES, parseAmount } from '../portalFormat';
import BottomSheet from './BottomSheet';
import {
  CameraIcon, CopyIcon, ScanIcon, WarningIcon,
} from './Icons';

const NOTE_MAX = 500;

/** A scan that never happened, so a failure renders as decision 3 above. */
const SCAN_UNAVAILABLE = Object.freeze({
  readable: false,
  read: null,
  unreadable_reasons: ['scan_unavailable'],
  duplicate_of: null,
});

/**
 * One row of the "A fotó alapján" panel. `value` is rendered when the scan
 * read that field and `copy.scanMissing` when it did not — every field of
 * `read` is nullable on its own, and a blank row would read as "nothing was
 * found" even when the total was read and only the merchant was not.
 */
function ScanField({ label, value, missing }) {
  return (
    <div className="pp-scan__row">
      <span className="pp-scan__key">{label}</span>
      <span className={`pp-scan__val${value ? '' : ' pp-scan__val--missing'}`}>
        {value || missing}
      </span>
    </div>
  );
}

/**
 * `copy` holds every word on the sheet: the driver's Hungarian by default
 * (portalFormat's EXPENSE_COPY), the operator screens' English when passed.
 */
export default function ExpenseSheet({
  bookingId = '', tripLabel = '', onClose, onSaved, copy = EXPENSE_COPY,
}) {
  const [type, setType] = useState('parking');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('HUF');
  const [note, setNote] = useState('');
  const [photo, setPhoto] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // The scan's answer for the photo currently attached, or null before there
  // is one (and while a retake is in flight).
  const [scan, setScan] = useState(null);
  const [scanning, setScanning] = useState(false);
  // "Ez egy másik blokk" — the driver has looked at the receipt we matched
  // and says this is not it. Reset by every new photo.
  const [notDuplicate, setNotDuplicate] = useState(false);
  // A ref, not state: two taps in the same frame both see stale state.
  const inFlight = useRef(false);
  // Whether the driver has edited the money fields by hand. A reading may
  // fill a field they have not touched and may replace its own earlier
  // prefill on a retake, but it must never overwrite something they typed —
  // that would be the scan taking authorship of their claim.
  const typedByHand = useRef({ amount: false, currency: false });
  // Which photo the reading in flight belongs to. A driver on airport wifi
  // who retakes before the first scan has answered has TWO in flight, and
  // they do not have to come back in order — the first one landing last would
  // prefill the amount off a photo that is no longer attached, with no sign
  // anything was wrong. Only the newest token's answer is allowed to land.
  const scanToken = useRef(0);

  const onPhoto = useCallback(async (event) => {
    const file = event.target.files && event.target.files[0];
    // Reset so choosing the same file again (a retake that failed) still fires.
    // eslint-disable-next-line no-param-reassign
    event.target.value = '';
    if (!file) return;

    setProcessing(true);
    setError('');
    let shrunk;
    try {
      shrunk = await downscaleImage(file);
    } catch {
      setError(copy.photoUnreadable);
      setProcessing(false);
      return;
    }
    // A new photo invalidates every judgement made about the last one.
    setPhoto(shrunk);
    setScan(null);
    setNotDuplicate(false);
    setProcessing(false);

    const token = scanToken.current + 1;
    scanToken.current = token;
    setScanning(true);
    let reading;
    try {
      // An answer with no body reads as a scan that never happened rather
      // than taking the sheet down with it: the driver still has a receipt to
      // file, and `scan_unavailable` is exactly what this is.
      reading = (await scanPortalExpensePhoto(shrunk)) || SCAN_UNAVAILABLE;
    } catch {
      reading = SCAN_UNAVAILABLE;
    }
    if (scanToken.current !== token) return;
    setScanning(false);
    setScan(reading);

    const read = reading.read || {};
    if (read.total != null && !typedByHand.current.amount) {
      setAmount(String(read.total));
      // The currency travels with the total or not at all: prefilling '12.50'
      // off a EUR receipt while HUF stays selected files a 12 Ft parking
      // ticket, which is a worse answer than no prefill.
      if (read.currency && !typedByHand.current.currency
        && EXPENSE_CURRENCIES.includes(read.currency)) {
        setCurrency(read.currency);
      }
    }
  }, [copy]);

  const onAmount = useCallback((event) => {
    typedByHand.current.amount = true;
    setAmount(event.target.value);
  }, []);

  const pickCurrency = useCallback((code) => {
    typedByHand.current.currency = true;
    setCurrency(code);
  }, []);

  const submit = useCallback(async (event) => {
    event.preventDefault();
    // GATED ON `scanning`, deliberately, and it was not always so. The first
    // cut left submit open during the scan on the reasoning that a driver who
    // has typed the amount has no use for the reading. That is true right up
    // until they submit a second before it lands: the row files with no
    // `photo_sha256` match to the reading the server is still writing, so the
    // dispatcher gets no badge, no duplicate check and no receipt date on a
    // photo that scanned perfectly well — the three things the feature is for,
    // lost to a race the driver cannot see.
    //
    // The wait is ~1-2s and it is NOT silent: the scan panel above says
    // "Blokk beolvasása…", the submit reads `scanWait`, and the amount's
    // placeholder says the total is coming. The field stays editable
    // throughout, so a driver who would rather type it than wait still can —
    // `typedByHand` keeps the reading from overwriting them.
    if (inFlight.current || processing || scanning) return;

    const value = parseAmount(amount);
    if (!Number.isFinite(value)) {
      setError(copy.amountMissing);
      return;
    }
    if (!photo) {
      setError(copy.photoMissing);
      return;
    }

    inFlight.current = true;
    setSaving(true);
    setError('');
    try {
      const created = await createPortalExpense({
        type, amount: value, currency, note, booking_id: bookingId, photo,
      });
      inFlight.current = false;
      onSaved(created);
      onClose();
    } catch (failure) {
      inFlight.current = false;
      setSaving(false);
      setError(copy.submitError(failure));
    }
  }, [amount, bookingId, copy, currency, note, onClose, onSaved, photo, processing, type]);

  const busy = saving || processing;
  const read = scan?.readable ? scan.read : null;
  const unreadable = Boolean(scan) && !scan.readable;
  // The one thing that holds a submit back, and only until the driver answers
  // it. An unreadable photo does NOT: see rule 2 above.
  const duplicate = notDuplicate ? null : scan?.duplicate_of;
  // Both of these answer the same three states in the same order, and the
  // scanning one comes FIRST: while the scan is in flight the submit is
  // disabled, and a disabled button whose label has not changed is
  // indistinguishable from a broken one.
  let submitLabel = copy.submit;
  let amountPlaceholder = '0';
  if (scanning) {
    submitLabel = copy.scanWait;
    amountPlaceholder = copy.amountScanning;
  } else if (unreadable) {
    submitLabel = copy.sendAnyway;
    amountPlaceholder = copy.amountPlaceholder;
  }

  return (
    <BottomSheet
      title={copy.title}
      subtitle={tripLabel ? copy.forTrip(tripLabel) : ''}
      onClose={onClose}
      closeDisabled={saving}
      closeLabel={copy.closeLabel}
    >
      <form className="pp-form" onSubmit={submit} noValidate>
        <div className="pp-field">
          <span className="pp-field__label" id="pp-expense-type">{copy.typeLabel}</span>
          <div className="pp-seg" role="group" aria-labelledby="pp-expense-type">
            {copy.types.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`pp-seg__item${type === option.value ? ' pp-seg__item--on' : ''}`}
                aria-pressed={type === option.value}
                onClick={() => setType(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/*
          The file input stays in the accessibility tree (visually hidden, not
          display:none) and both the big "Fotó a blokkról" target and the
          retake control are <label>s for it. Label activation is the one way
          to open the camera that works on every mobile browser — a scripted
          .click() on a hidden input is ignored by some iOS versions. The
          "Új fotó készítése" button in the unreadable panel below is a label
          for the same reason, which is why it is not a <button>.
        */}
        <div className="pp-field pp-photo">
          <input
            id="pp-expense-photo"
            className="visually-hidden"
            type="file"
            accept="image/*"
            capture="environment"
            onChange={onPhoto}
            disabled={busy}
          />
          {photo ? (
            <div className="pp-photo__taken">
              <img
                className="pp-photo__thumb"
                src={`data:${photo.type};base64,${photo.data}`}
                alt={copy.photoAlt}
              />
              <div className="pp-photo__side">
                <span className="pp-photo__ok">{copy.photoReady}</span>
                <label
                  htmlFor="pp-expense-photo"
                  className={`pp-btn pp-btn--outline${busy ? ' pp-btn--inert' : ''}`}
                >
                  <CameraIcon size={18} />
                  {processing ? copy.processing : copy.retake}
                </label>
              </div>
            </div>
          ) : (
            <label
              htmlFor="pp-expense-photo"
              className={`pp-photo__pick${processing ? ' pp-photo__pick--busy' : ''}`}
            >
              <CameraIcon size={26} />
              <span>{processing ? copy.processingPhoto : copy.pickPhoto}</span>
            </label>
          )}
        </div>

        {/*
          WHAT THE PHOTO SAID — one block, three answers, in the same place on
          the sheet whichever one it is.

          ONLY THE TWO THAT CHANGE WHAT THE DRIVER MUST DO are live regions
          (`role="status"`, polite, so neither cuts across somebody already
          typing the amount): an unreadable photo relabels the submit, and a
          possible duplicate disables it. The reading itself is not announced
          separately — it has already reached the driver through the amount
          field, whose value it filled and whose `aria-describedby` hint says
          where that value came from. Saying it twice is what a
          visually-hidden announcer did here before it was removed.

          NOT `role="alert"`, which the sheet reserves for the submit error:
          there is exactly one alert on the sheet and that is how it is found.
          Nor is the Toast's own `role="status"` crowded, because neither of
          these panels can be on screen once the sheet has closed on a save.
        */}
        {scanning ? (
          <div className="pp-scan pp-scan--busy">
            <ScanIcon size={16} />
            <span className="pp-scan__title">{copy.scanning}</span>
          </div>
        ) : null}

        {read ? (
          <div className="pp-scan">
            <div className="pp-scan__head">
              <ScanIcon size={16} />
              <span className="pp-scan__title">{copy.scanTitle}</span>
            </div>
            <div className="pp-scan__fields">
              <ScanField
                label={copy.scanAmountLabel}
                value={read.total != null ? copy.amount(read.total, read.currency || currency) : ''}
                missing={copy.scanMissing}
              />
              <ScanField
                label={copy.scanDateLabel}
                value={copy.when(read.occurred_at)}
                missing={copy.scanMissing}
              />
              <ScanField
                label={copy.scanPlaceLabel}
                value={read.merchant || ''}
                missing={copy.scanMissing}
              />
            </div>
          </div>
        ) : null}

        {unreadable ? (
          <div className="pp-scan pp-scan--warn" role="status">
            <div className="pp-scan__head">
              <WarningIcon size={17} />
              <span className="pp-scan__title">{copy.unreadableTitle}</span>
            </div>
            <ul className="pp-scan__reasons">
              {copy.unreadableReasons(scan.unreadable_reasons).map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
            <label
              htmlFor="pp-expense-photo"
              className={`pp-btn pp-btn--dark pp-btn--block${busy ? ' pp-btn--inert' : ''}`}
            >
              <CameraIcon size={18} />
              {copy.retakePhoto}
            </label>
          </div>
        ) : null}

        {duplicate ? (
          <div className="pp-scan pp-scan--dup" role="status">
            <div className="pp-scan__head">
              <CopyIcon size={16} />
              <span className="pp-scan__title">{copy.duplicateTitle}</span>
            </div>
            <div className="pp-scan__fields">
              <span className="pp-scan__dup-what">
                {`${copy.typeName(duplicate.type)} · ${copy.amount(duplicate.amount, duplicate.currency)}`}
              </span>
              <span className="pp-scan__dup-when">
                {copy.duplicateWhen(copy.when(duplicate.created_at))}
              </span>
              <span className="pp-scan__dup-when">{copy.duplicateSame}</span>
            </div>
            <button
              type="button"
              className="pp-btn pp-btn--dark pp-btn--block"
              onClick={() => setNotDuplicate(true)}
            >
              {copy.duplicateDifferent}
            </button>
            <button
              type="button"
              className="pp-btn pp-btn--outline pp-btn--block"
              onClick={onClose}
              disabled={saving}
            >
              {copy.duplicateCancel}
            </button>
          </div>
        ) : null}

        <div className="pp-field">
          <label className="pp-field__label" htmlFor="pp-expense-amount">{copy.amountLabel}</label>
          <div className="pp-amount">
            <input
              id="pp-expense-amount"
              className="pp-input pp-amount__input"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              enterKeyHint="done"
              placeholder={amountPlaceholder}
              value={amount}
              onChange={onAmount}
              // NOT disabled while scanning: a driver who would rather type
              // the total than wait for it must be able to. `aria-busy` is
              // what tells a screen reader the field is about to fill itself.
              aria-busy={scanning || undefined}
              aria-describedby={read?.total != null ? 'pp-expense-amount-hint' : undefined}
            />
            <div className="pp-seg pp-seg--compact" role="group" aria-label={copy.currencyLabel}>
              {EXPENSE_CURRENCIES.map((code) => (
                <button
                  key={code}
                  type="button"
                  className={`pp-seg__item${currency === code ? ' pp-seg__item--on' : ''}`}
                  aria-pressed={currency === code}
                  onClick={() => pickCurrency(code)}
                >
                  {code}
                </button>
              ))}
            </div>
          </div>
          {read?.total != null ? (
            <span className="pp-field__hint" id="pp-expense-amount-hint">{copy.scanHint}</span>
          ) : null}
        </div>

        <div className="pp-field">
          <label className="pp-field__label" htmlFor="pp-expense-note">
            {copy.noteLabel}
            <span className="pp-field__optional">{copy.optional}</span>
          </label>
          <textarea
            id="pp-expense-note"
            className="pp-input pp-input--area"
            rows={2}
            maxLength={NOTE_MAX}
            placeholder={copy.notePlaceholder}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </div>

        {error ? <div className="pp-form__error" role="alert">{error}</div> : null}

        {/*
          The way out of an unreadable photo: quieter than the retake above,
          never absent, and it says what the office will see. Pressing it
          submits exactly what a clean scan would have submitted.
        */}
        <button
          type="submit"
          className={`pp-btn pp-btn--block pp-btn--tall ${unreadable ? 'pp-btn--outline' : 'pp-btn--primary'}`}
          disabled={busy || scanning || Boolean(duplicate)}
        >
          {saving ? copy.sending : submitLabel}
        </button>
        {unreadable ? (
          <span className="pp-scan__foot">{copy.sendAnywayNote}</span>
        ) : null}
      </form>
    </BottomSheet>
  );
}
